/* ORCHID — the scroll layer.
 *
 * One rAF-throttled scroll loop that gives a group of planes differential
 * movement, so a frame reads as having depth rather than being a flat picture
 * with text on it.
 *
 * Markup contract, entirely additive:
 *
 *   <div data-orchid-depth>                     the frame the progress is measured against
 *     <img data-orchid-plane="0.9">             rate, in tenths of the frame's travel
 *     <div data-orchid-plane="-0.35">…</div>    negative leads the scroll, positive lags it
 *   </div>
 *
 * The rate is multiplied by 100px, so 0.9 is 90px of total travel across the
 * whole pass. Anything past about 200px stops reading as depth and starts
 * reading as a bug, which is why the rates here are small and why the script
 * clamps them.
 *
 * Written to the same rules as orchid.js: no images inside the JavaScript (the
 * content filter rewrites those in flight and breaks the file), transform and
 * opacity only, idempotent init so the theme editor can re-run it, and a hard
 * stop under prefers-reduced-motion.
 */
(function () {
  'use strict';

  if (window.orchidScrollLoaded) return;
  window.orchidScrollLoaded = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var bound = new WeakSet();
  var frames = [];
  var ticking = false;

  var MAX_RATE = 2;   // 200px of travel, the point past which depth reads as a fault

  function collect() {
    var found = document.querySelectorAll('[data-orchid-depth]');
    Array.prototype.forEach.call(found, function (frame) {
      if (bound.has(frame)) return;
      bound.add(frame);
      var planes = Array.prototype.map.call(
        frame.querySelectorAll('[data-orchid-plane]'),
        function (el) {
          var rate = parseFloat(el.getAttribute('data-orchid-plane'));
          if (isNaN(rate)) rate = 0;
          if (rate > MAX_RATE) rate = MAX_RATE;
          if (rate < -MAX_RATE) rate = -MAX_RATE;
          return { el: el, rate: rate };
        }
      );
      if (planes.length) frames.push({ frame: frame, planes: planes });
    });
  }

  function apply() {
    ticking = false;
    var vh = window.innerHeight || document.documentElement.clientHeight;

    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      var rect = f.frame.getBoundingClientRect();

      // Off screen in either direction: leave the last value alone rather than
      // writing a transform every frame for something nobody is looking at.
      if (rect.bottom < -vh || rect.top > vh * 2) continue;

      // Progress from "frame's top edge at the bottom of the viewport" to
      // "frame's bottom edge at the top of it", centred so that p = 0 is the
      // frame sitting in the middle of the screen. Planes are therefore at
      // their neutral position when the frame is the thing being looked at,
      // which is the only moment the composition has to be exactly right.
      var span = rect.height + vh;
      var p = (vh - rect.top) / span;      // 0 entering, 1 leaving
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      var centred = p - 0.5;

      for (var j = 0; j < f.planes.length; j++) {
        var pl = f.planes[j];
        var y = pl.rate * centred * 100;
        pl.el.style.setProperty('--orchid-plane-y', y.toFixed(2) + 'px');
      }
    }
  }

  var queuedAt = 0;

  function onScroll() {
    if (ticking) {
      // A throttled or unpainted tab can drop the animation frame entirely, and
      // a plain boolean latch then strands every later scroll behind a callback
      // that will never run. Measured: in a hidden browser pane rAF fired zero
      // times across two seconds. Clear the latch if the frame never arrived.
      if (Date.now() - queuedAt < 250) return;
      ticking = false;
    }
    ticking = true;
    queuedAt = Date.now();
    window.requestAnimationFrame(apply);
  }

  function init() {
    if (reduced) return;
    collect();
    if (!frames.length) return;
    apply();
  }

  if (!reduced) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders a section without a page load; the old nodes go
  // with it, so re-collect and drop any frame whose element has been detached.
  document.addEventListener('shopify:section:load', function () {
    frames = frames.filter(function (f) { return f.frame.isConnected; });
    init();
  });
})();
